// src/Dashboard/pages/user/ArbitrageJudo/components/ArbitrageQuiz.jsx

import React, { useMemo, useState } from "react";
import {
  FaCheck,
  FaChevronRight,
  FaRedoAlt,
  FaTimes,
  FaTrophy,
} from "react-icons/fa";

import { ARBITRAGE_QUIZ_QUESTIONS } from "../data/arbitrageJudoData";
import { shuffleArray } from "../utils/arbitrageJudoUtils";

export default function ArbitrageQuiz() {
  const [quizKey, setQuizKey] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [answers, setAnswers] = useState([]);

  const questions = useMemo(() => {
    return shuffleArray(ARBITRAGE_QUIZ_QUESTIONS).slice(0, 10);
  }, [quizKey]);

  const currentQuestion = questions[currentIndex];
  const isFinished = answers.length === questions.length;
  const score = answers.filter((item) => item.isCorrect).length;
  const progress = Math.round((answers.length / questions.length) * 100);

  const handleAnswer = (answer) => {
    if (selectedAnswer) return;

    setSelectedAnswer(answer);

    const isCorrect = answer === currentQuestion.answer;

    setAnswers((prev) => [
      ...prev,
      {
        questionId: currentQuestion.id,
        question: currentQuestion.question,
        selected: answer,
        correct: currentQuestion.answer,
        explanation: currentQuestion.explanation,
        isCorrect,
      },
    ]);
  };

  const handleNext = () => {
    if (!selectedAnswer) return;

    if (currentIndex < questions.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setSelectedAnswer("");
    }
  };

  const restart = () => {
    setQuizKey((prev) => prev + 1);
    setCurrentIndex(0);
    setSelectedAnswer("");
    setAnswers([]);
  };

  if (isFinished) {
    const rate = Math.round((score / questions.length) * 100);

    return (
      <div className="space-y-5">
        <div className="rounded-[28px] bg-slate-950 p-6 text-white">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white/80 ring-1 ring-white/15">
                <FaTrophy className="text-amber-300" />
                Résultats du QCM
              </p>

              <h3 className="mt-3 text-3xl font-bold">
                {score} / {questions.length}
              </h3>

              <p className="mt-1 text-sm text-white/70">
                Taux de réussite : {rate} %
              </p>
            </div>

            <button
              type="button"
              onClick={restart}
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-100"
            >
              <FaRedoAlt />
              Recommencer
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {answers.map((answer, index) => (
            <div
              key={answer.questionId}
              className={`rounded-3xl border p-4 ${
                answer.isCorrect
                  ? "border-emerald-200 bg-emerald-50 dark:border-emerald-800/60 dark:bg-emerald-900/20"
                  : "border-rose-200 bg-rose-50 dark:border-rose-800/60 dark:bg-rose-900/20"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`grid h-9 w-9 shrink-0 place-items-center rounded-2xl text-white ${
                    answer.isCorrect ? "bg-emerald-500" : "bg-rose-500"
                  }`}
                >
                  {answer.isCorrect ? <FaCheck /> : <FaTimes />}
                </span>

                <div>
                  <p className="text-sm font-bold text-slate-950 dark:text-white">
                    {index + 1}. {answer.question}
                  </p>

                  <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                    Votre réponse :{" "}
                    <span className="font-bold">{answer.selected}</span>
                  </p>

                  {!answer.isCorrect && (
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      Bonne réponse :{" "}
                      <span className="font-bold">{answer.correct}</span>
                    </p>
                  )}

                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-400">
                    {answer.explanation}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const selectedAnswerData = answers.find(
    (answer) => answer.questionId === currentQuestion.id
  );

  return (
    <div className="space-y-5">
      <div className="rounded-[28px] border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950 sm:p-5">
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-slate-900 dark:text-white">
            Question {currentIndex + 1} / {questions.length}
          </p>

          <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
            Score : {score}
          </p>
        </div>

        <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F] transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="rounded-[30px] border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950 sm:p-6">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-slate-400">
          Choisissez la bonne réponse
        </p>

        <h3 className="mt-2 text-xl font-bold leading-snug text-slate-950 dark:text-white">
          {currentQuestion.question}
        </h3>

        <div className="mt-5 grid gap-3">
          {currentQuestion.options.map((option) => {
            const isSelected = selectedAnswer === option;
            const isCorrect = selectedAnswer && option === currentQuestion.answer;
            const isWrong = isSelected && option !== currentQuestion.answer;

            return (
              <button
                key={option}
                type="button"
                onClick={() => handleAnswer(option)}
                disabled={!!selectedAnswer}
                className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-4 text-left text-sm font-bold transition ${
                  isCorrect
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200"
                    : isWrong
                    ? "border-rose-300 bg-rose-50 text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200"
                    : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
                }`}
              >
                <span>{option}</span>

                {isCorrect && <FaCheck />}
                {isWrong && <FaTimes />}
              </button>
            );
          })}
        </div>

        {selectedAnswerData && (
          <div
            className={`mt-5 rounded-3xl p-4 text-sm leading-6 ${
              selectedAnswerData.isCorrect
                ? "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-200 dark:ring-emerald-800/60"
                : "bg-rose-50 text-rose-800 ring-1 ring-rose-200 dark:bg-rose-900/20 dark:text-rose-200 dark:ring-rose-800/60"
            }`}
          >
            <p className="font-bold">
              {selectedAnswerData.isCorrect ? "Bonne réponse !" : "À revoir"}
            </p>
            <p className="mt-1">{currentQuestion.explanation}</p>
          </div>
        )}

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={handleNext}
            disabled={!selectedAnswer || currentIndex >= questions.length - 1}
            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-bold text-white transition hover:bg-[#013E8E] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-slate-950 dark:hover:bg-blue-100"
          >
            Question suivante
            <FaChevronRight />
          </button>
        </div>
      </div>
    </div>
  );
}